import { Component, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import { MatSort } from '@angular/material/sort';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { AdministrativeConfigurations } from 'src/app/models/Admin/AdministrativeConfigurations.model';
import { TempFileModel } from 'src/app/models/Admin/TempFileModel';
import { AdministrativeService } from 'src/app/services/administrative/administrative.service';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateTime } from "luxon";
import { TempFileStatusEnum } from 'src/app/Enums/TempFileStatusEnum';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';

@Component({
  selector: 'app-adminsignin',
  templateUrl: './adminsignin.page.html',
  styleUrls: ['./adminsignin.page.scss'],
})
export class AdminSignInPage {
  @ViewChild('outerSort', { static: true }) sort!: MatSort;

  public isLogged: boolean = false;
  public token = "";
  public enableAccountValidationServiceFlag:string="account-validation-service-enable";
  public configurations: AdministrativeConfigurations[] = [];
  public dataSource!: MatTableDataSource<AdministrativeConfigurations>;
  public readonly displayedColumns: string[] = [
    "column-flag",
    "column-id",
    "column-link",
    "column-options"
  ];

  public tempFileStatusEnum = TempFileStatusEnum;
  public tempFiles: TempFileModel[] = [];
  public dataSourceTempFile!: MatTableDataSource<TempFileModel>;
  public readonly tempFileColumns: string[] = [
    "companyEmail",
    "userName",
    "companyDocument",
    "pdvIdDescription",
    "dateUpload",
    "fileName",
    "fileSize",
    "status",
    "download"
  ];

  public formGroup = new FormGroup({
    beginDate: new FormControl(DateTime.now().minus({days: 7}).toJSDate(), [Validators.required]),
    endDate: new FormControl(DateTime.now().toJSDate(), [Validators.required]),
  });

  public appName: string;

  constructor(
    private authenticationService: AuthenticationService, 
    private snackBar: MatSnackBar, 
    private translateService: TranslateService,
    private adminService: AdministrativeService,
    private dateAdapter: DateAdapter<Date>,
    public messageService: InvalidFieldMessageService,
    private appConfig: AppConfigService
  ) {
    this.appName = this.appConfig.appName;
    this.dateAdapter.setLocale("pt-BR");
    this.formGroup.controls.beginDate.valueChanges.subscribe(_ => { this.validateDateFields(); });
    this.formGroup.controls.endDate.valueChanges.subscribe(_ => { this.validateDateFields(); });
  }

  public async signup() {
    try {
      if(this.token.length > 0) {
        const isLoginCorrect = await this.authenticationService.administrativeSignIn(this.token)
        if(isLoginCorrect) {
          this.isLogged = true;
        }
      }
    } catch(err) {
      this.snackBar.open(this.translateService.instant("admin.invalidLogin"), '', {
        duration: 10000,
        verticalPosition: 'top',
      panelClass: ['red-snackbar']
      });
      this.isLogged = false;
    }
    if (this.isLogged) {
      await this.getAllConfigurations()
      await this.getTempFiles();
    }
  }

  private async getAllConfigurations() {
    this.configurations =  await this.adminService.getAllAdministrativeConfigurations();
    if(this.configurations.length) {
      this.dataSource = new MatTableDataSource(this.configurations)
      this.dataSource.sort = this.sort;
    }
  }

  public editConfigurationLink(config: AdministrativeConfigurations): void {
    config.configurationBackupLink = config.link;

    config.editingLink = true;
  }

  public cancelLinkEdition(config: AdministrativeConfigurations): void {
    config.link = config.configurationBackupLink!;
    config.editingLink = false;
  }

  public async saveLinkEdition(config: AdministrativeConfigurations): Promise<void> {
    if(config.link) {
 
      if(config.flag===this.enableAccountValidationServiceFlag){

        if(!this.validateFlagServiceEnableValue(config.link)){
          this.showFlagEnableServiceInvalidValueError()
          return;
        }
      }
      let success: boolean = await this.adminService.editLink(this.token, config.flag, config.link)
      if(success) {
        config.configurationBackupLink = config.link;
        config.editingLink = false;
      }
    } else {
      this.cancelLinkEdition(config);
    }
  }

  public validateDateFields() {
    const begin = this.formGroup.controls.beginDate;
    const end = this.formGroup.controls.endDate;
    begin.setErrors(null);
    end.setErrors(null);

    if (!begin.value){
      begin.setErrors({'invalid': true});
      return;
    }

    if (!end.value){
      end.setErrors({'invalid': true});
      return;
    }

    if (begin.value > end.value) {
      begin.setErrors({ 'startDateGreaterThanFinalDate': true });
      end.setErrors({ 'endDateMinorThanStartDate': true });
      return;
    }

    if (begin.value > DateTime.now().toJSDate()){
      begin.setErrors({ 'startDateGreaterThanToday': true });
      return;
    }
  }

  public clickFilterButton() {
    this.formGroup.markAllAsTouched();
    

    if (this.formGroup.invalid) return;

    this.getTempFiles();
  }

  public async getTempFiles() {
    const beginDate = this.formGroup.controls.beginDate.value!;
    const endDate = this.formGroup.controls.endDate.value!;
    this.tempFiles = await this.adminService.getTempFiles(beginDate, endDate);
    this.dataSourceTempFile = new MatTableDataSource(this.tempFiles)
  }
  public clickDownloadButton(url: string) {
    window.open(url, "_blank");
  }

  public parseDate(value: string): Date | null {
    try {
      const parsedDate = DateTime.fromFormat(value, 'dd/MM/yyyy').toJSDate();
      return parsedDate;
    } catch (e) {
      return null;
    }
  }

  public changeBeginDate(event: any) {
    const parsedDate = this.parseDate(event.target.value);
    if (parsedDate) {
      this.formGroup.controls["beginDate"].setValue(parsedDate);
    }
  }

  public changeEndDate(event: any) {
    const parsedDate = this.parseDate(event.target.value);
    if (parsedDate) {
      this.formGroup.controls["endDate"].setValue(parsedDate);
    }
  }
  private validateFlagServiceEnableValue(value:string|undefined|null):boolean{

    if(value==null || value== undefined)
      return false;

    if(value!='0' &&value!='1')
      return false;

    return true;
  }
  private  showFlagEnableServiceInvalidValueError():void{
      this.snackBar.open(this.translateService.instant("admin.invalidFlagServiceValue"), '', {
                duration: 10000,
                verticalPosition: 'top',
              panelClass: ['red-snackbar']
      });
  }
}